import React, { useState, useEffect } from 'react';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { stripePromise } from '../config/stripe';
import { supabase } from '../supabase';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';

// Supabase Edge Function URL (permanent serverless backend)
const SUPABASE_FUNCTIONS_URL = 'https://oatwrcgucondsxooeyuk.supabase.co/functions/v1';

const StripePaymentForm = ({ orderData, onPaymentSuccess, onPaymentError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Test if Supabase Edge Function is accessible
  const testBackend = React.useCallback(async () => {
    const testEndpoint = `${SUPABASE_FUNCTIONS_URL}/create-payment-intent`;
    console.log('🧪 Testing Supabase Edge Function endpoint:', testEndpoint);
    
    try {
      const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9hdHdyY2d1Y29uZHN4b29leXVrIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTM1MzMyODQsImV4cCI6MjA2OTEwOTI4NH0.vYfXWMki5BurQay0OB_avtKBLwOtWinj4UWfEW963EY';
      
      // Create headers object - ensure apikey is sent correctly
      const testHeaders = new Headers();
      testHeaders.set('Content-Type', 'application/json');
      testHeaders.set('apikey', supabaseAnonKey);
      testHeaders.set('Authorization', `Bearer ${supabaseAnonKey}`);
      
      const testResponse = await fetch(testEndpoint, {
        method: 'POST',
        headers: testHeaders,
        body: JSON.stringify({
          amount: 1000,
          currency: 'usd',
          order_number: 'TEST-123',
        }),
      });
      
      console.log('🧪 Test response status:', testResponse.status);
      console.log('🧪 Test response ok:', testResponse.ok);
      
      let testData;
      try {
        testData = await testResponse.json();
      } catch (e) {
        const text = await testResponse.text();
        testData = { error: text || 'No JSON response', status: testResponse.status };
      }
      
      console.log('🧪 Test response data:', testData);
      
      if (testResponse.ok && testData.clientSecret) {
        console.log('✅ Supabase Edge Function is working!');
        return true;
      } else {
        console.error('❌ Edge Function test failed:', {
          status: testResponse.status,
          statusText: testResponse.statusText,
          data: testData
        });
        
        // Provide specific error message
        if (testResponse.status === 404) {
          console.error('❌ Function not found (404). Deploy it in Supabase Dashboard → Edge Functions.');
        } else if (testResponse.status === 500) {
          console.error('❌ Function error (500). Check if STRIPE_SECRET_KEY is set in Supabase secrets.');
        } else if (testResponse.status === 401) {
          console.error('❌ Unauthorized (401). Check apikey header.');
        } else if (testResponse.status === 0) {
          console.error('❌ Network error. Check internet connection.');
        }
        
        return false;
      }
    } catch (testErr) {
      console.error('❌ Edge Function test error:', testErr);
      console.error('Error details:', {
        message: testErr.message,
        name: testErr.name,
        stack: testErr.stack
      });
      
      // Check for specific error types
      if (testErr.message.includes('Failed to fetch') || testErr.message.includes('NetworkError')) {
        console.error('❌ Network error - Cannot reach Supabase Edge Function.');
      }
      
      return false;
    }
  }, []);

  // Debug: Log when component renders and Stripe status
  useEffect(() => {
    console.log('🔵 StripePaymentForm rendered');
    console.log('Stripe status:', { 
      stripe: !!stripe, 
      elements: !!elements,
      orderData: !!orderData,
      orderTotal: orderData?.total 
    });
    
    if (!stripe) {
      console.warn('⚠️ Stripe is not loaded yet');
    }
    if (!elements) {
      console.warn('⚠️ Elements is not loaded yet');
    }
    
    // Test Supabase Edge Function when component loads (only once)
    if (orderData && orderData.customer_email) {
      console.log('🧪 Testing Supabase Edge Function connection...');
      testBackend().then(isWorking => {
        if (!isWorking) {
          console.warn('⚠️ Edge Function test failed. Make sure it is deployed and secret key is set.');
        } else {
          console.log('✅ Supabase Edge Function is ready!');
        }
      });
    }
  }, [stripe, elements, orderData, testBackend]);

  // Helper function to convert country name to ISO 3166-1 alpha-2 country code
  const getCountryCode = (country) => {
    if (!country) return 'US'; // Default to US
    
    const countryLower = country.trim().toLowerCase();
    
    // United States
    if (countryLower.includes('united states') || countryLower.includes('usa') || countryLower.includes('america')) {
      return 'US';
    }
    // United Kingdom
    if (countryLower.includes('united kingdom') || countryLower.includes('uk') || countryLower.includes('britain') || countryLower.includes('england')) {
      return 'GB';
    }
    // UAE
    if (countryLower.includes('uae') || countryLower.includes('united arab emirates') || countryLower.includes('emirates')) {
      return 'AE';
    }
    // Saudi Arabia
    if (countryLower.includes('saudi') || countryLower.includes('ksa') || countryLower.includes('arabia')) {
      return 'SA';
    }
    // Canada
    if (countryLower.includes('canada')) {
      return 'CA';
    }
    // Australia
    if (countryLower.includes('australia')) {
      return 'AU';
    }
    // France
    if (countryLower.includes('france')) {
      return 'FR';
    }
    // Germany
    if (countryLower.includes('germany') || countryLower.includes('deutschland')) {
      return 'DE';
    }
    // India
    if (countryLower.includes('india')) {
      return 'IN';
    }
    
    // Default to US if country not recognized
    console.warn(`Country "${country}" not recognized, defaulting to US`);
    return 'US';
  };

  // Helper function to convert currency symbol to Stripe currency code
  const getStripeCurrency = (country) => {
    const countryLower = country?.toLowerCase() || '';
    if (countryLower.includes('united states') || countryLower.includes('usa') || countryLower.includes('america')) {
      return 'usd';
    } else if (countryLower.includes('united kingdom') || countryLower.includes('uk') || countryLower.includes('britain') || countryLower.includes('england')) {
      return 'gbp';
    } else if (countryLower.includes('uae') || countryLower.includes('united arab emirates') || countryLower.includes('emirates')) {
      return 'aed';
    } else if (countryLower.includes('saudi') || countryLower.includes('ksa') || countryLower.includes('arabia')) {
      return 'sar';
    }
    return 'usd'; // Default to USD
  };


  // Create payment intent via backend endpoint (SECURE - RECOMMENDED)
  const createPaymentIntent = async () => {
    try {
      const stripeCurrency = getStripeCurrency(orderData.customer_country);
      const amountInSmallestUnit = Math.round(orderData.total * 100);
      
      console.log('📦 Creating payment intent with:', {
        amount: amountInSmallestUnit,
        currency: stripeCurrency,
        order_number: orderData.order_number,
        total: orderData.total
      });
      
      // Use Supabase Edge Function (permanent serverless backend)
      const endpoint = `${SUPABASE_FUNCTIONS_URL}/create-payment-intent`;
      
      console.log('🌐 Calling Supabase Edge Function:', endpoint);
      
      const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9hdHdyY2d1Y29uZHN4b29leXVrIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTM1MzMyODQsImV4cCI6MjA2OTEwOTI4NH0.vYfXWMki5BurQay0OB_avtKBLwOtWinj4UWfEW963EY';
      
      // Create headers object - ensure apikey is sent correctly
      const headers = new Headers();
      headers.set('Content-Type', 'application/json');
      headers.set('apikey', supabaseAnonKey);
      headers.set('Authorization', `Bearer ${supabaseAnonKey}`);
      
      console.log('📤 Sending headers:', {
        'Content-Type': headers.get('Content-Type'),
        'apikey': headers.get('apikey') ? 'present' : 'missing',
        'Authorization': headers.get('Authorization') ? 'present' : 'missing'
      });
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          amount: amountInSmallestUnit,
          currency: stripeCurrency,
          order_number: orderData.order_number,
        }),
      });

      console.log('📡 Response status:', response.status, response.statusText);
      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          const text = await response.text();
          errorData = { error: text || `HTTP ${response.status}: ${response.statusText}` };
        }
        
        console.error('❌ Error response:', errorData);
        const errorMessage = errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
        
        // Check for CORS or connection errors
        if (response.status === 0 || errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
          throw new Error('Cannot connect to Supabase Edge Function. Please check: 1) Function is deployed, 2) Secret key is set, 3) Internet connection.');
        }
        
        if (response.status === 404) {
          throw new Error('Edge Function not found. Please deploy the function in Supabase Dashboard.');
        }
        
        if (response.status === 500) {
          throw new Error('Edge Function error. Check Supabase Dashboard → Edge Functions → Logs for details.');
        }
        
        if (response.status === 401) {
          throw new Error('Unauthorized. Check if STRIPE_SECRET_KEY is set in Supabase secrets.');
        }
        
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      console.log('✅ Payment intent response:', responseData);
      
      const { clientSecret } = responseData;
      if (!clientSecret) {
        throw new Error('No client secret received from Edge Function. Check function logs in Supabase Dashboard.');
      }
      
      console.log('✅ Client secret received successfully');
      return clientSecret;
    } catch (err) {
      console.error('❌ Error creating payment intent:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      throw err;
    }
  };

  const handleSubmit = async (event) => {
    console.log('🚀 Payment form submitted!');
    console.log('Event:', event);
    console.log('Stripe loaded:', !!stripe);
    console.log('Elements loaded:', !!elements);
    console.log('Order data:', orderData);
    
    event.preventDefault();
    event.stopPropagation(); // Prevent form from bubbling up to parent form
    
    // Validate order data
    if (!orderData || !orderData.customer_first_name || !orderData.customer_email || !orderData.customer_address) {
      const errorMsg = 'Please complete all shipping information before payment.';
      console.error('❌ Incomplete order data');
      setError(errorMsg);
      onPaymentError(new Error(errorMsg));
      return;
    }
    
    if (!stripe || !elements) {
      const errorMsg = 'Stripe is not loaded. Please refresh the page and try again.';
      console.error('❌ Stripe not loaded:', { stripe: !!stripe, elements: !!elements });
      setError(errorMsg);
      return;
    }

    console.log('✅ Stripe and Elements are loaded, starting payment process...');
    setIsProcessing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      console.log('📝 Getting card elements...');
      const cardNumberElement = elements.getElement(CardNumberElement);
      const cardExpiryElement = elements.getElement(CardExpiryElement);
      const cardCvcElement = elements.getElement(CardCvcElement);
      
      if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
        throw new Error('Card elements not found. Please enter your card details.');
      }
      
      console.log('💳 Creating payment method...');
      
      // Convert country name to ISO country code
      const countryCode = getCountryCode(orderData.customer_country);
      console.log(`🌍 Converting country "${orderData.customer_country}" to code: ${countryCode}`);
      
      // Create payment method using card number element (Stripe handles the rest)
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: {
          name: `${orderData.customer_first_name} ${orderData.customer_last_name}`,
          email: orderData.customer_email,
          phone: orderData.customer_phone,
          address: {
            line1: orderData.customer_address,
            city: orderData.customer_city,
            state: orderData.customer_state,
            postal_code: orderData.customer_zip_code,
            country: countryCode, // Use ISO country code instead of country name
          },
        },
      });

      if (pmError) {
        const errorMessage = pmError.message || 'Invalid card details. Please check your card information and try again.';
        setError(errorMessage);
        setIsProcessing(false);
        onPaymentError(new Error(errorMessage));
        return;
      }

      // Create and confirm payment intent
      try {
        console.log('Creating payment intent...');
        const clientSecret = await createPaymentIntent();
        console.log('Payment intent created, client secret received');
        
        if (!clientSecret) {
          throw new Error('No client secret received from server');
        }
        
        // Confirm payment with PaymentIntent
        console.log('Confirming payment...');
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: paymentMethod.id,
          }
        );
        
        console.log('Payment confirmation result:', { confirmError, paymentIntent });

        if (confirmError) {
          let errorMessage = 'Payment failed. ';
          if (confirmError.type === 'card_error') {
            errorMessage += confirmError.message || 'Your card was declined. Please try a different payment method.';
          } else if (confirmError.type === 'validation_error') {
            errorMessage += 'Please check your card details and try again.';
          } else {
            errorMessage += confirmError.message || 'Please try again or contact support.';
          }
          setError(errorMessage);
          setIsProcessing(false);
          onPaymentError(new Error(errorMessage));
          return;
        }

        if (paymentIntent.status === 'succeeded') {
          // Show success message
          setError(null);
          setSuccessMessage('Payment successful! Processing your order...');
          setIsProcessing(true);
          await processOrder(paymentIntent.id, paymentIntent);
        } else {
          const errorMessage = `Payment status: ${paymentIntent.status}. Please contact support if you were charged.`;
          setError(errorMessage);
          setIsProcessing(false);
          onPaymentError(new Error(errorMessage));
        }
      } catch (intentError) {
        console.error('Payment intent error:', intentError);
        console.error('Error details:', {
          message: intentError.message,
          stack: intentError.stack,
          name: intentError.name
        });
        
        let errorMessage = 'Failed to process payment. ';
        if (intentError.message && intentError.message.includes('Cannot connect')) {
          errorMessage += 'Cannot connect to payment server. Please ensure the backend is running.';
        } else if (intentError.message && intentError.message.includes('CORS')) {
          errorMessage += 'Server configuration error. Please contact support.';
        } else if (intentError.message) {
          errorMessage += intentError.message;
        } else {
          errorMessage += 'An unexpected error occurred. Please try again.';
        }
        
        setError(errorMessage);
        setIsProcessing(false);
        onPaymentError(new Error(errorMessage));
        return; // Important: return to prevent further execution
      }
      
    } catch (err) {
      console.error('Payment error:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      
      const errorMessage = err.message || 'Payment failed. Please try again.';
      setError(errorMessage);
      setIsProcessing(false);
      onPaymentError(err);
    }
  };

  const processOrder = async (paymentIntentId, paymentIntent) => {
    try {
      // Group items by vendor
      const itemsByVendor = {};
      orderData.items.forEach(item => {
        const vendorId = item.vendor_id || 'no_vendor';
        if (!itemsByVendor[vendorId]) {
          itemsByVendor[vendorId] = [];
        }
        itemsByVendor[vendorId].push(item);
      });

      console.log('Order vendor analysis:', {
        totalItems: orderData.items.length,
        vendorsInOrder: Object.keys(itemsByVendor),
        itemsByVendor: itemsByVendor
      });

      // Create separate orders for each vendor
      const savedOrders = [];
      for (const [vendorId, vendorItems] of Object.entries(itemsByVendor)) {
        // Calculate totals for this vendor's items
        const vendorSubtotal = vendorItems.reduce((total, item) => total + (item.price * item.quantity), 0);
        const vendorShipping = vendorId === 'no_vendor' ? 0 : (orderData.shipping / Object.keys(itemsByVendor).length);
        const vendorTotal = vendorSubtotal + vendorShipping;

        const orderToSave = {
          customer_first_name: orderData.customer_first_name,
          customer_last_name: orderData.customer_last_name,
          customer_email: orderData.customer_email,
          customer_phone: orderData.customer_phone,
          customer_address: orderData.customer_address,
          customer_city: orderData.customer_city,
          customer_state: orderData.customer_state,
          customer_zip_code: orderData.customer_zip_code,
          customer_country: orderData.customer_country,
          payment_method: 'stripe',
          items: vendorItems,
          subtotal: vendorSubtotal,
          tax: 0,
          shipping: vendorShipping,
          total: vendorTotal,
          order_number: `${orderData.order_number}-${vendorId === 'no_vendor' ? 'NV' : vendorId}`,
          status: 'confirmed',
          vendor_id: vendorId === 'no_vendor' ? null : vendorId,
          payment_status: 'completed',
          transaction_id: paymentIntentId // Store Stripe payment intent ID in transaction_id field
        };

        console.log(`Saving order for vendor ${vendorId}:`, orderToSave);

        const { data: savedOrder, error } = await supabase
          .from('orders')
          .insert([orderToSave])
          .select()
          .single();

        if (error) {
          console.error(`Supabase insert error for vendor ${vendorId}:`, error);
          console.error('Error details:', error.message, error.details, error.hint);
          throw error;
        }

        savedOrders.push(savedOrder);
        console.log(`Order created successfully for vendor ${vendorId}:`, savedOrder);
      }

      // Use the first saved order for navigation (or the one with the highest total)
      const primaryOrder = savedOrders.reduce((prev, current) => 
        parseFloat(prev.total) > parseFloat(current.total) ? prev : current
      );

      // Clear cart and redirect
      clearCart();
      onPaymentSuccess({ id: paymentIntentId });
      
      navigate(`/thank-you/${primaryOrder.id}`, {
        state: {
          orderNumber: primaryOrder.order_number,
          orderId: primaryOrder.id,
          items: orderData.items,
          total: orderData.total,
          paymentMethod: 'Stripe',
          transactionId: paymentIntentId,
          allOrders: savedOrders
        }
      });

    } catch (error) {
      console.error('Order processing error:', error);
      throw error;
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <div className="stripe-payment-wrapper" onClick={(e) => e.stopPropagation()}>
    <form 
      onSubmit={handleSubmit} 
      className="stripe-payment-form"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.stopPropagation();
        }
      }}
    >
      <div className="stripe-card-fields">
        <div className="stripe-field-row">
          <label>Card Number</label>
          <div className="stripe-card-element stripe-card-number">
            <CardNumberElement options={cardElementOptions} />
          </div>
        </div>
        
        <div className="stripe-field-row-group">
          <div className="stripe-field-row">
            <label>Expiry Date</label>
            <div className="stripe-card-element stripe-card-expiry">
              <CardExpiryElement options={cardElementOptions} />
            </div>
          </div>
          
          <div className="stripe-field-row">
            <label>CVC</label>
            <div className="stripe-card-element stripe-card-cvc">
              <CardCvcElement options={cardElementOptions} />
            </div>
          </div>
        </div>
      </div>
      
      {successMessage && (
        <div className="stripe-success">
          <p>✅ {successMessage}</p>
        </div>
      )}
      
      {error && (
        <div className="stripe-error">
          <p>{error}</p>
        </div>
      )}
      
      <button
        type="button"
        onClick={(e) => {
          console.log('🔘🔘🔘 PAY BUTTON CLICKED! 🔘🔘🔘');
          console.log('Event:', e);
          console.log('Button state:', { 
            stripe: !!stripe, 
            elements: !!elements,
            isProcessing, 
            orderData: !!orderData,
            orderTotal: orderData?.total
          });
          
          e.preventDefault();
          e.stopPropagation();
          
          // Call handleSubmit directly
          const fakeEvent = { 
            preventDefault: () => console.log('preventDefault called'),
            stopPropagation: () => console.log('stopPropagation called')
          };
          
          console.log('Calling handleSubmit...');
          handleSubmit(fakeEvent);
        }}
        disabled={!stripe || isProcessing || !orderData}
        className="stripe-submit-button"
        style={{ cursor: (!stripe || isProcessing || !orderData) ? 'not-allowed' : 'pointer' }}
      >
        {isProcessing ? 'Processing...' : orderData ? `Pay ${orderData.total.toFixed(2)}` : 'Loading...'}
      </button>
      
      {isProcessing && (
        <div className="payment-processing">
          <div className="processing-spinner"></div>
          <p>Processing your payment...</p>
        </div>
      )}
    </form>
    </div>
  );
};

const StripePayment = ({ orderData, onPaymentSuccess, onPaymentError }) => {
  return (
    <Elements stripe={stripePromise}>
      <StripePaymentForm
        orderData={orderData}
        onPaymentSuccess={onPaymentSuccess}
        onPaymentError={onPaymentError}
      />
    </Elements>
  );
};

export default StripePayment;

